import { useQuery } from '@apollo/client'
import ShareIcon from '@mui/icons-material/Share'
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import { useToursQuery } from '@typings/graphql'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { ShareDialog } from '../components/ShareDialog'
import { VoiceoverPanel } from '../components/VoiceoverPanel'
import {
  TOUR_MODE_VOICEOVER_QUERY,
  TourModeVoiceoverData,
  TourModeVoiceoverVars
} from '../graphql'
import { extractVoiceovers } from '../lib/extractVoiceovers'
import { slugify } from '../lib/slugify'

export const VoiceoverView: React.FC = () => {
  const { tourId: tourIdParam } = useParams<{ tourId?: string }>()
  const { data: toursData, loading: toursLoading } = useToursQuery()
  const tours = toursData?.tours ?? []

  const [tourId, setTourId] = useState(tourIdParam ?? '')
  const [modeId, setModeId] = useState('')
  const [ext, setExt] = useState('aac')
  const [shareOpen, setShareOpen] = useState(false)

  const selectedTour = useMemo(
    () => tours.find((t) => t.id === tourId),
    [tours, tourId]
  )
  const modes = selectedTour?.modes ?? []

  // Preselect the param tour once tours have loaded.
  useEffect(() => {
    if (tourIdParam && tours.some((t) => t.id === tourIdParam)) {
      setTourId(tourIdParam)
    }
  }, [tourIdParam, tours])

  // Default to the first mode whenever the tour changes.
  useEffect(() => {
    if (modes.length > 0 && !modes.some((m) => m.id === modeId)) {
      setModeId(modes[0].id)
    }
  }, [modes, modeId])

  const { data, loading, error } = useQuery<
    TourModeVoiceoverData,
    TourModeVoiceoverVars
  >(TOUR_MODE_VOICEOVER_QUERY, {
    variables: { id: modeId },
    skip: !modeId
  })

  const rows = useMemo(
    () => extractVoiceovers(data?.tourMode, ext),
    [data?.tourMode, ext]
  )

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden'
      }}
    >
      <Typography variant="h4" sx={{ flex: 0 }}>
        Voiceovers
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flex: 0 }}>
        <TextField
          select
          size="small"
          label="Tour"
          value={tourId}
          onChange={(e) => setTourId(e.target.value)}
          sx={{ minWidth: 280 }}
          disabled={toursLoading}
        >
          {tours.map((tour) => (
            <MenuItem key={tour.id} value={tour.id}>
              {tour.title}
            </MenuItem>
          ))}
        </TextField>

        {modes.length > 1
          ? (
            <TextField
              select
              size="small"
              label="Modus"
              value={modeId}
              onChange={(e) => setModeId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              {modes.map((mode) => (
                <MenuItem key={mode.id} value={mode.id}>
                  {mode.title}
                </MenuItem>
              ))}
            </TextField>
            )
          : null}

        <TextField
          select
          size="small"
          label="Dateiendung"
          value={ext}
          onChange={(e) => setExt(e.target.value)}
          sx={{ minWidth: 140 }}
          helperText="für die Vorschläge"
        >
          {['aac', 'mp3', 'm4a', 'wav'].map((e) => (
            <MenuItem key={e} value={e}>
              .{e}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {!tourId
          ? (
            <Typography color="text.secondary">
              Bitte eine Tour auswählen.
            </Typography>
            )
          : loading
            ? (
              <CircularProgress />
              )
            : error
              ? (
                <Typography color="error">
                  Tour konnte nicht geladen werden: {error.message}
                </Typography>
                )
              : (
                <VoiceoverPanel
                  rows={rows}
                  modeTitle={data?.tourMode?.title ?? ''}
                  storageKey={modeId}
                  csvFileName={`voiceovers_${slugify(selectedTour?.title ?? 'tour')}.csv`}
                  headerActions={
                    <Button
                      startIcon={<ShareIcon />}
                      variant="contained"
                      disabled={!modeId}
                      onClick={() => setShareOpen(true)}
                    >
                      Teilen
                    </Button>
            }
                />
                )}
      </Box>

      {shareOpen && tourId && modeId
        ? (
          <ShareDialog
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            tourId={tourId}
            modeId={modeId}
            ext={ext}
          />
          )
        : null}
    </Box>
  )
}
